import ServiceFactory from "../../service";
import account from "../../data/account";
import endpoints from "../../endpoints";
import { AuthBase } from "../../../plunk/service/auth-base";

/*
 * Custom Reporting shared bits (CUSTOM_REPORTING.md; UX note §223-§226).
 * API: /v1/reports* on AnalyticsService — staff JWT + x-app-id.
 *
 * Rules baked in:
 *  - The PICKER renders ONLY what /v1/reports/registry returns — money
 *    metrics are simply absent for managed tenants, never greyed (§224).
 *  - Validation/role 400s and 403s carry a plain `error` string —
 *    surface it verbatim (§224/§226).
 *  - Rows are positional arrays matching `columns`; dates are
 *    'yyyy-MM-dd' strings; panels ≤ 8, rows ≤ 500.
 */

export function repSvc(api) {
  const Service = ServiceFactory.getType(`/reports${api}`, "product"); // /v1 root
  return new Service();
}
export async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
export function apiRoot() {
  return endpoints.product.root; // '/v1' base used by the reports rail
}

// Raw authenticated fetch — used where the service layer can't help
// (CSV blob download, share.plunk.io mint).
export function authHeaders() {
  return {
    Authorization: "Bearer " + (AuthBase.idToken || ""),
    "x-app-id": (account.get() || {}).tenantId || ""
  };
}
export function errText(e, fallback) {
  try {
    const b = JSON.parse(e.message);
    if (b.error) return b.error;
  } catch (x) {/* raw */}
  return typeof e?.message === "string" && e.message.length < 200 ? e.message : fallback;
}

// Roles (§226 item 2): update/delete/schedule → owner or administrator
// (starters admin-only); server enforces regardless — this only hides
// destructive actions client-side.
export function isPlatformAdmin() {
  return ((account.get() || {}).groups || []).some(g => ["owner", "administrator", "root"].includes(String(g).toLowerCase()));
}
export function canManageReport(row) {
  if (!row) return false;
  // Owners and administrators manage every report, starters included (a starter is just a seeded row).
  if (isPlatformAdmin()) return true;
  if (row.starter) return false;
  // §280.2 — reports saved while the backend dropped the email claim
  // carry createdBy NULL; an orphaned row would otherwise show no
  // Edit/Delete for anyone. The server enforces the real rule.
  if (!row.createdBy) return true;
  const me = account.getEmail && account.getEmail() || (account.get() || {}).email || "";
  return String(row.createdBy || "").toLowerCase() === String(me).toLowerCase();
}
export function rangeLabel(token) {
  if (/^custom_\d{8}_\d{8}$/.test(String(token || ""))) {
    const m = String(token).match(/^custom_(\d{4})(\d{2})(\d{2})_(\d{4})(\d{2})(\d{2})$/);
    return `${m[2]}/${m[3]}/${m[1]} – ${m[5]}/${m[6]}/${m[4]}`;
  }
  return String(token || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
export function runQs(range) {
  const m = /^custom_(\d{4})(\d{2})(\d{2})_(\d{4})(\d{2})(\d{2})$/.exec(String(range || ""));
  if (m) return `from=${m[1]}-${m[2]}-${m[3]}&to=${m[4]}-${m[5]}-${m[6]}`;
  return range ? `range=${encodeURIComponent(range)}` : "";
}
export function metricLabel(registry, dataset, key) {
  const d = (registry?.datasets || []).find(x => x.key === dataset);
  const hit = d && (d.metrics || []).concat(d.dimensions || []).find(mm => mm.key === key);
  return hit && hit.label || String(key || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
export function fmtCell(v) {
  if (v == null) return "—";
  if (typeof v === "number") {
    // §280.4 decimal consistency: floats always render ONE decimal
    // (never a raw double, never sometimes-0-sometimes-1).
    if (!Number.isInteger(v)) return v.toLocaleString("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
    return v.toLocaleString("en-US");
  }
  return String(v);
}

// §280.4 — money columns. The registry does NOT carry a money flag today
// (probed 2026-08-31: metrics are {key,label} only — the flag ask is
// filed), so the key heuristic is the interim: it covers every money
// metric in the current registry (revenue, aov, avg_unit_price,
// revenue_per_customer) plus the campaign money keys managed tenants
// never receive. Swap to the flag when it lands.
export function isMoneyKey(key) {
  return /(revenue|aov|price|value|spend|budget|cost)/i.test(String(key || ""));
}
export function fmtMoney(v) {
  if (v == null) return "—";
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toLocaleString("en-US", {
    maximumFractionDigits: 1
  })}M`;
  return `$${n.toLocaleString("en-US", {
    maximumFractionDigits: 0
  })}`;
}
// Percentage metrics (CTR, conversion rate, …) can be sub-1 (a 0.96% CTR),
// where the §280.4 one-decimal rule rounds them to a misleading "1.0". Keep up
// to 2 decimals for these; the metric label already carries the "%".
export function isPctKey(key) {
  return /(ctr|rate|pct|percent)/i.test(String(key || ""));
}
export function fmtPct(v) {
  if (v == null) return "—";
  return (Number(v) || 0).toLocaleString("en-US", {
    maximumFractionDigits: 2
  });
}
export function fmtByKey(key, v) {
  // Dimension values (rep, dealer, region, lifecycle stage, lapse risk) are
  // strings: render them as-is, whatever the key looks like, so a key that
  // happens to match the money/percent heuristics never prints "$0".
  if (typeof v === "string" && v !== "" && !Number.isFinite(Number(v))) return v;
  if (isMoneyKey(key)) return fmtMoney(v);
  if (isPctKey(key)) return fmtPct(v);
  return fmtCell(v);
}

// §280.7c — honest KPI deltas: a prior period that exists but is zero (or
// a zero current) says so instead of silently dropping the chip.
export function kpiDelta(cur, prevRow, ci) {
  if (!prevRow) return undefined;
  const pv = Number(prevRow[ci]) || 0;
  const c = Number(cur) || 0;
  if (pv > 0) {
    const d = (c - pv) / pv * 100;
    return {
      dir: d >= 0.05 ? "up" : d <= -0.05 ? "down" : "flat",
      text: `${d >= 0 ? "+" : ""}${d.toFixed(1)}% vs prev`
    };
  }
  if (c > 0) return {
    dir: "flat",
    text: "no prior data"
  };
  return {
    dir: "flat",
    text: "— no prior data"
  };
}

// §281 — panel width tokens (FE-owned strings; null = full, nothing moves).
// kpiDelta in the kit's Delta shape ({dir, pct}); null = no prior data ("no comparison").
export function kitKpiDelta(cur, prevRow, ci) {
  if (!prevRow) return null;
  const pv = Number(prevRow[ci]) || 0;
  const c = Number(cur) || 0;
  if (pv <= 0) return null;
  const d = (c - pv) / pv * 100;
  return {
    dir: d >= 0.05 ? "up" : d <= -0.05 ? "down" : "flat",
    pct: d
  };
}
export function widthSpan(w) {
  return w === "third" ? 2 : w === "half" ? 3 : 6; // 6-col grid
}
export function defaultWidth(kind) {
  return kind === "line" || kind === "bar" ? "half" : "full";
}